import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Button, Descriptions, Tag, message, Card, Spin, Space, Popconfirm, Tabs, Upload, Form, Input, Select, Modal, Table, Avatar, Tooltip, Radio, Typography, InputNumber,
} from 'antd';
import {
  ArrowLeftOutlined, EditOutlined, DeleteOutlined, SaveOutlined, UploadOutlined, DownloadOutlined, FileTextOutlined, UserAddOutlined, QuestionCircleOutlined, CopyOutlined,
} from '@ant-design/icons';
import { applicationsApi, dictionariesApi, employeesApi, usersApi } from '../services/api-services';
import { useStreamGeneration } from '../hooks/useStreamGeneration';
import { useTabStore } from '../store/tabStore';
import { resolveUrl } from '../utils/url';
import AppFileManager from '../components/AppFileManager';
import MdEditor from 'react-markdown-editor-lite';
import MarkdownIt from 'markdown-it';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'react-markdown-editor-lite/lib/index.css';
import '../styles/markdown.css';
import ImagePicker from '../components/ImagePicker';

const mdParser = new MarkdownIt();

const ApplicationDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const defaultTab = (location.state as any)?.activeTab || 'info';
  const { addTab } = useTabStore();
  const { generate: streamGenerate } = useStreamGeneration();
  const [app, setApp] = useState<any>(null);
  const [appId, setAppId] = useState<number | null>(/^\d+$/.test(slug || '') ? Number(slug) : null);
  const [loading, setLoading] = useState(false);
  const [isEditingDoc, setIsEditingDoc] = useState(false);
  const [editedDoc, setEditedDoc] = useState('');
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [infoForm] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [fileManagerKey, setFileManagerKey] = useState(0);
  const [appCategoryOptions, setAppCategoryOptions] = useState<{ label: string; value: string }[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [agentMapping, setAgentMapping] = useState<Record<string, string>>({});
  const [detectedNames, setDetectedNames] = useState<{name: string; description: string; response_format?: string}[]>([]);
  const [pluginSettings, setPluginSettings] = useState<any[]>([]);
  const [pluginSettingsValues, setPluginSettingsValues] = useState<Record<string, any>>({});
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configEditText, setConfigEditText] = useState('');
  const [configJsonError, setConfigJsonError] = useState('');
  const [readmeContent, setReadmeContent] = useState('');
  const [showReadmeModal, setShowReadmeModal] = useState(false);
  const [showPluginPromptModal, setShowPluginPromptModal] = useState(false);
  const [pluginPromptText, setPluginPromptText] = useState('');
  const [generatingPlugin, setGeneratingPlugin] = useState(false);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [addPermModal, setAddPermModal] = useState(false);
  const [permForm] = Form.useForm();
  const [integrationForm] = Form.useForm();
  const [savingIntegration, setSavingIntegration] = useState(false);
  const watchedCallback = Form.useWatch('callback_url', integrationForm);
  const watchedSecret = Form.useWatch('secret', integrationForm);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isFullscreen = !!document.querySelector('.rc-md-editor.full');
      document.documentElement.classList.toggle('md-fullscreen', isFullscreen);
    });
    observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class'] });
    return () => {
      observer.disconnect();
      document.documentElement.classList.remove('md-fullscreen');
    };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const fetchApp = /^\d+$/.test(slug || '') ? applicationsApi.get(Number(slug)) : applicationsApi.getBySlug(slug!);
      const res: any = await fetchApp;
      setApp(res);
      setAppId(res.id);
      addTab({ key: `/app/${slug}/info`, path: `/app/${slug}/info`, label: `详情:${res.name}`, closable: true });
    } catch (e) {
      message.error('获取应用详情失败');
    }
    setLoading(false);
  }, [slug, addTab]);

  useEffect(() => {
    if (slug) {
      addTab({ key: `/app/${slug}/info`, path: `/app/${slug}/info`, label: '应用详情', closable: true });
      fetchData();
    }
    dictionariesApi.getByCode('app_category').then((dict: any) => {
      if (dict?.items) setAppCategoryOptions(dict.items.map((i: any) => ({ label: i.name, value: i.value })));
    }).catch(() => {});
    employeesApi.list({ limit: 200 }).then((res: any) => {
      setEmployees(Array.isArray(res) ? res : res?.data || []);
    }).catch(() => {});
    usersApi.list({ limit: 500 }).then((res: any) => setAllUsers(Array.isArray(res) ? res : [])).catch(() => {});
  }, [slug, fetchData, addTab]);

  useEffect(() => {
    if (app?.config?.agent_mapping) {
      setAgentMapping(app.config.agent_mapping);
    }
    if (app) {
      const integration = app.config?.integration || {};
      integrationForm.setFieldsValue({
        callback_url: integration.callback_url || '',
        secret: integration.secret || '',
        cache_ttl: integration.cache_ttl ?? 60,
      });
    }
    if (app && appId) {
      applicationsApi.permissions(appId).then((res: any) => setPermissions(Array.isArray(res) ? res : [])).catch(() => {});
      applicationsApi.getAgentNames(appId).then((res: any) => {
        const items: {name: string; description: string}[] = Array.isArray(res) ? res : res?.data || [];
        setDetectedNames(items);
        setAgentMapping((prev) => {
          const merged = { ...prev };
          items.forEach((item) => { if (!(item.name in merged)) merged[item.name] = ''; });
          return merged;
        });
      }).catch(() => {});
      applicationsApi.getPluginSettings(appId).then((res: any) => {
        setPluginSettings(Array.isArray(res) ? res : []);
      }).catch(() => {});
      applicationsApi.getPluginSettingsValues(appId).then((res: any) => {
        setPluginSettingsValues(res && typeof res === 'object' && !Array.isArray(res) ? res : {});
      }).catch(() => {});
    }
  }, [app, appId]);

  const handleDelete = async () => {
    try {
      await applicationsApi.delete(appId!);
      message.success('删除成功');
      window.dispatchEvent(new Event('applications-updated'));
      navigate('/applications');
    } catch (e) {
      message.error('删除失败');
    }
  };

  const handleUninstall = async () => {
    try {
      const res: any = await applicationsApi.uninstall(appId!);
      message.success(res?.data?.warning ? `卸载完成（${res.data.warning}）` : '插件源文件及数据表已清理');
      window.dispatchEvent(new Event('applications-updated'));
      setFileManagerKey((k) => k + 1);
      fetchData();
    } catch (e) {
      message.error('卸载失败');
    }
  };

  const handleEditInfo = () => {
    infoForm.setFieldsValue({
      name: app.name,
      icon: app.icon || '',
      description: app.description || '',
      tags: app.tags || [],
      app_type: app.app_type || 'subapp',
    });
    setIsEditingInfo(true);
  };

  const handleSaveInfo = async () => {
    try {
      const values = await infoForm.validateFields();
      await applicationsApi.update(appId!, values);
      message.success('保存成功');
      setIsEditingInfo(false);
      fetchData();
    } catch (e: any) {
      if (e.response) {
        message.error(e.response?.data?.detail || '保存失败');
      }
    }
  };

  const editModal = (
    <Modal
      title="编辑应用信息"
      open={isEditingInfo}
      onCancel={() => setIsEditingInfo(false)}
      onOk={handleSaveInfo}
      okText="保存"
      cancelText="取消"
      destroyOnClose
    >
      <Form form={infoForm} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item label="名称" name="name" rules={[{ required: true, message: '请输入应用名称' }]}>
          <Input />
        </Form.Item>
        <Form.Item
          label={(
            <span>
              应用类型
              <Tooltip title="子应用：带管理后台，可直接访问；后端应用：仅提供 sdk.js 供第三方对接。">
                <QuestionCircleOutlined style={{ marginLeft: 4, color: 'var(--apple-text-secondary, #999)', cursor: 'help' }} />
              </Tooltip>
            </span>
          )}
          name="app_type"
        >
          <Radio.Group>
            <Radio.Button value="subapp">子应用</Radio.Button>
            <Radio.Button value="backend">后端应用</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item label="图标" name="icon">
          <ImagePicker shape="square" />
        </Form.Item>
        <Form.Item label="描述" name="description">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item label="标签" name="tags">
          <Select mode="multiple" placeholder="选择或输入分类标签" options={appCategoryOptions} allowClear />
        </Form.Item>
      </Form>
    </Modal>
  );

  const handleSaveDoc = async () => {
    try {
      await applicationsApi.update(appId!, { documentation: editedDoc });
      message.success('保存成功');
      setIsEditingDoc(false);
      fetchData();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '保存失败');
    }
  };

  const handleGenerateDoc = () => {
    // 退出编辑模式
    setIsEditingDoc(false);
    let collectedContent = '';
    
    streamGenerate({
      action: 'create_task',
      payload: { 
        key: 'generate_application',
        id: appId,
        resourceName: `${app?.name || '应用'} - 介绍生成`,
      },
      navigationUrl: `/app/${slug}/info`,
      onChunk: (chunk: string) => {
        collectedContent += chunk;
      },
      onDone: async () => {
        // 提取应用概述内容
        const overviewMatch = collectedContent.match(/## 应用概述\s*\n([\s\S]*?)(?=\n## |$)/);
        const overviewContent = overviewMatch ? overviewMatch[1].trim() : '';
        
        // 自动保存
        try {
          await applicationsApi.update(appId!, { documentation: collectedContent });
          // 如果提取到概述内容，同时更新 description
          if (overviewContent && app) {
            await applicationsApi.update(appId!, { description: overviewContent });
          }
          fetchData();
        } catch (e: any) {
          message.error(e.response?.data?.detail || '保存失败');
        }
      }
    });
  };

  const handleUploadStatic = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res: any = await applicationsApi.uploadStatic(appId!, formData);
      message.success('源文件上传成功');
      setFileManagerKey((k) => k + 1);
      fetchData();
      window.dispatchEvent(new Event('applications-updated'));
    } catch (e: any) {
      message.error(e.response?.data?.detail || '上传失败');
    }
    setUploading(false);
    return false;
  };

  if (loading && !app) {
    return (
      <div className="apple-page" style={{ textAlign: 'center', padding: '48px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!app) {
    return (
      <div className="apple-page">
        <div className="apple-empty">
          <p className="apple-empty-title">未找到应用</p>
          <Button type="primary" onClick={() => navigate('/applications')}>返回列表</Button>
        </div>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'info',
      label: '基础信息',
      children: (
        <>
          <Descriptions bordered column={1} size="small" style={{ marginBottom: 16, width: '100%' }}>
              <Descriptions.Item label="名称">{app.name}</Descriptions.Item>
              <Descriptions.Item label="应用标识">{app.slug || '-'}</Descriptions.Item>
              <Descriptions.Item label="应用类型">
                {app.app_type === 'backend'
                  ? <Tag color="purple">后端应用（仅 sdk.js 对接）</Tag>
                  : <Tag color="blue">子应用（带管理后台）</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="图标">
                {app.icon ? (
                  (app.icon.startsWith('http') || app.icon.startsWith('data:') || app.icon.startsWith('/uploads/') || app.icon.startsWith('/avatars/') || app.icon.startsWith('/app-icons/')) ? (
                    <img src={resolveUrl(app.icon)} alt={app.name} style={{ width: 32, height: 32, objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontSize: 28 }}>{app.icon}</span>
                  )
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="描述">{app.description || '-'}</Descriptions.Item>
              <Descriptions.Item label="标签">
                {app.tags && app.tags.length > 0
                  ? app.tags.map((tag: string, i: number) => <Tag key={i} style={{ margin: '0 4px 4px 0' }}>{tag}</Tag>)
                  : '-'}
              </Descriptions.Item>
            </Descriptions>

          <Card
            title="应用介绍"
            styles={{ body: { padding: 0, overflow: 'hidden', borderRadius: '0 0 8px 8px' } }}
            extra={
              isEditingDoc ? (
                <Space>
                  <Tooltip title="根据源文件生成应用介绍">
                    <Button onClick={handleGenerateDoc}>AI生成</Button>
                  </Tooltip>
                  <Button onClick={() => setIsEditingDoc(false)}>取消</Button>
                  <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveDoc}>保存</Button>
                </Space>
              ) : (
                <Button type="text" icon={<EditOutlined />} onClick={() => { setEditedDoc(app.documentation || ''); setIsEditingDoc(true); }} />
              )
            }
          >
            {isEditingDoc ? (
              <MdEditor
                value={editedDoc}
                style={{ height: '500px' }}
                renderHTML={(text) => mdParser.render(text)}
                onChange={({ text }) => setEditedDoc(text)}
                config={{
                  view: { menu: true, md: true, html: true },
                  canView: { menu: true, md: true, html: true, fullScreen: true, hideMenu: true },
                }}
              />
            ) : (
              <div className="markdown-preview">
                {app.documentation ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{app.documentation}</ReactMarkdown>
                ) : (
                  <p style={{ color: 'var(--apple-text-secondary)', padding: 24, textAlign: 'center' }}>暂无介绍，点击右上角"编辑"添加</p>
                )}
              </div>
            )}
          </Card>
        </>
      ),
    },
    {
      key: 'static',
      label: '源文件',
      children: (
        <Card>
          <div style={{ marginBottom: 24 }}>
            <Space style={{ marginBottom: 12 }}>
              <Upload
                accept=".zip"
                showUploadList={false}
                beforeUpload={(file) => { handleUploadStatic(file); return false; }}
              >
                <Button icon={<UploadOutlined />} loading={uploading}>上传安装包</Button>
              </Upload>
              <Popconfirm title="确定卸载？将清理插件源文件及创建的数据库表。" onConfirm={handleUninstall} okText="确定" cancelText="取消">
                <Button danger icon={<DeleteOutlined />}>卸载</Button>
              </Popconfirm>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => {
                  const baseUrl = process.env.REACT_APP_API_URL || '/api/v1';
                  const link = document.createElement('a');
                  link.href = `${baseUrl}/sub_app/template/download`;
                  link.click();
                }}
              >
                下载示例代码
              </Button>
              <Button
                icon={<FileTextOutlined />}
                onClick={async () => {
                  try {
                    const content = await applicationsApi.readFile(appId!, 'static/README.md');
                    setReadmeContent(typeof content === 'string' ? content : content?.data || '');
                    setShowReadmeModal(true);
                  } catch {
                    message.error('未找到 static/README.md');
                  }
                }}
              >
                查看说明文档
              </Button>
              <Button
                onClick={() => {
                  setPluginPromptText(app.plugin_prompt || '');
                  setShowPluginPromptModal(true);
                }}
              >
                AI生成插件
              </Button>
            </Space>
            <p style={{ color: 'var(--apple-text-secondary)', margin: 0 }}>
              上传应用源文件压缩包（.zip），子应用包含前端页面和 server.py，后端应用仅需 server.py。
            </p>
          </div>
          <AppFileManager key={fileManagerKey} appId={appId!} showDeleteButton={false} />
          <Modal
            title="说明文档 (static/README.md)"
            open={showReadmeModal}
            onCancel={() => setShowReadmeModal(false)}
            footer={null}
            width={1100}
          >
            <div className="markdown-preview" style={{ maxHeight: 'calc(80vh - 120px)', overflow: 'auto' }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{readmeContent}</ReactMarkdown>
            </div>
          </Modal>
          <Modal
            title="AI生成插件"
            open={showPluginPromptModal}
            onCancel={() => setShowPluginPromptModal(false)}
            width={900}
            footer={
              <Space>
                <Button onClick={() => setShowPluginPromptModal(false)}>取消</Button>
                <Button
                  type="default"
                  onClick={async () => {
                    try {
                      await applicationsApi.update(appId!, { plugin_prompt: pluginPromptText });
                      message.success('保存成功');
                      fetchData();
                    } catch (e: any) {
                      message.error(e.response?.data?.detail || '保存失败');
                    }
                  }}
                >
                  保存
                </Button>
                <Button
                  type="primary"
                  loading={generatingPlugin}
                  onClick={async () => {
                    try {
                      setGeneratingPlugin(true);
                      await applicationsApi.update(appId!, { plugin_prompt: pluginPromptText });
                      message.warning('插件生成功能暂时不可用，正在升级中');
                      setShowPluginPromptModal(false);
                    } catch (e: any) {
                      message.error(e.response?.data?.detail || '保存失败');
                    } finally {
                      setGeneratingPlugin(false);
                    }
                  }}
                >
                  生成应用
                </Button>
              </Space>
            }
          >
            <MdEditor
              value={pluginPromptText}
              style={{ height: '500px' }}
              renderHTML={(text) => mdParser.render(text)}
              onChange={({ text }) => setPluginPromptText(text)}
              config={{
                view: { menu: true, md: true, html: true },
                canView: { menu: true, md: true, html: true, fullScreen: true, hideMenu: true },
              }}
            />
          </Modal>
        </Card>
      ),
    },
    {
      key: 'agent-mapping',
      label: '插件配置',
      children: (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={async () => {
                  try {
                    const config = { ...(app.config || {}), agent_mapping: agentMapping };
                    await applicationsApi.update(appId!, { config });
                    if (pluginSettings.length > 0) {
                      await applicationsApi.savePluginSettingsValues(appId!, pluginSettingsValues);
                    }
                    message.success('配置保存成功');
                    fetchData();
                  } catch (e: any) {
                    message.error(e.response?.data?.detail || '保存失败');
                  }
                }}
              >
                保存
              </Button>
              <Button icon={<FileTextOutlined />} onClick={() => {
                setConfigEditText(app.config ? JSON.stringify(app.config, null, 2) : '{}');
                setConfigJsonError('');
                setShowConfigModal(true);
              }}>
                查看配置
              </Button>
            </Space>
          </div>
          <Card title="数字人映射" style={{ marginBottom: 16 }} extra={
            <span style={{ color: 'var(--apple-text-secondary)', fontSize: 12 }}>
              自动从 server.py 中检测 agent_names 声明
            </span>
          }>
            {detectedNames.length === 0 && Object.keys(agentMapping).length === 0 ? (
              <p style={{ color: 'var(--apple-text-secondary)' }}>未检测到数字人调用，请确保 server.py 中声明了 agent_names 变量</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 380px))', gap: 16 }}>
                {Object.entries(agentMapping).map(([logicalName, empIdentifier]) => {
                  const meta = detectedNames.find((d) => d.name === logicalName);
                  const selectedEmp = employees.find((e: any) => e.identifier === empIdentifier);
                  return (
                    <Card
                      key={logicalName}
                      size="small"
                      style={{ borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
                      bodyStyle={{ padding: '22px 24px' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                        <Tag color="blue" style={{ margin: 0, fontFamily: 'monospace', fontSize: 12 }}>{logicalName}</Tag>
                        {!meta && <Tag color="orange" style={{ margin: 0 }}>已失效</Tag>}
                      </div>
                      {meta?.description && (
                        <div style={{ color: 'var(--apple-text-secondary)', fontSize: 12, marginBottom: 8, lineHeight: 1.6 }}>
                          功能描述：{meta.description}
                        </div>
                      )}
                      {meta?.response_format ? (
                        <div style={{ marginBottom: 14, fontSize: 12, color: 'var(--apple-text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ flexShrink: 0 }}>返回格式：</span>
                          <Tooltip title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxWidth: 320 }}>{meta.response_format}</pre>}>
                            <div style={{
                              fontFamily: 'monospace',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              cursor: 'pointer',
                              flex: 1,
                              minWidth: 0,
                            }}>{meta.response_format}</div>
                          </Tooltip>
                        </div>
                      ) : (
                        <div style={{ marginBottom: 14, fontSize: 12, color: 'var(--apple-text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ flexShrink: 0 }}>返回格式：</span>
                          <span style={{ color: 'var(--apple-text-tertiary, #aaa)' }}>无</span>
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: 'var(--apple-text-secondary)', marginBottom: 6 }}>选择数字人</div>
                      <Select
                        value={empIdentifier || undefined}
                        placeholder="选择数字人"
                        style={{ width: '100%' }}
                        allowClear
                        onChange={(val) => setAgentMapping((prev) => ({ ...prev, [logicalName]: val || '' }))}
                        options={employees.map((e: any) => ({ label: `${e.name} (${e.identifier})`, value: e.identifier }))}
                      />
                      {empIdentifier && (
                        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                          {selectedEmp ? (
                            <>
                              <Avatar size={32} src={selectedEmp.avatar ? resolveUrl(selectedEmp.avatar) : undefined}>
                                {selectedEmp.name?.[0]}
                              </Avatar>
                              <span style={{ fontSize: 13, color: 'var(--apple-text-primary, #1d1d1f)', fontWeight: 500 }}>{selectedEmp.name}</span>
                            </>
                          ) : (
                            <>
                              <Avatar size={32} style={{ background: '#f5f5f5', color: '#bbb' }}>?</Avatar>
                              <span style={{ fontSize: 13, color: '#bbb' }}>{empIdentifier}（无效）</span>
                            </>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </Card>
          {pluginSettings.length > 0 && (
            <Card title="参数配置" style={{ marginBottom: 16 }} extra={
              <span style={{ color: 'var(--apple-text-secondary)', fontSize: 12 }}>
                由 server.py 中 plugin_settings 声明
              </span>
            }>
              <div style={{ maxWidth: 600 }}>
                {pluginSettings.map((item: any) => (
                  <div key={item.key} style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                      {item.label || item.key}
                      {item.required && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>}
                    </label>
                    {item.description && (
                      <div style={{ color: 'var(--apple-text-secondary)', fontSize: 12, marginBottom: 4 }}>{item.description}</div>
                    )}
                    {item.type === 'select' ? (
                      <Select
                        value={pluginSettingsValues[item.key] ?? item.default ?? undefined}
                        placeholder={item.placeholder || `请选择${item.label || item.key}`}
                        style={{ width: '100%' }}
                        allowClear
                        onChange={(val) => setPluginSettingsValues((prev) => ({ ...prev, [item.key]: val }))}
                        options={(item.options || []).map((opt: any) =>
                          typeof opt === 'string' ? { label: opt, value: opt } : opt
                        )}
                      />
                    ) : item.type === 'textarea' ? (
                      <Input.TextArea
                        rows={3}
                        value={pluginSettingsValues[item.key] ?? item.default ?? ''}
                        placeholder={item.placeholder || ''}
                        onChange={(e) => setPluginSettingsValues((prev) => ({ ...prev, [item.key]: e.target.value }))}
                      />
                    ) : (
                      <Input
                        type={item.type === 'number' ? 'number' : 'text'}
                        value={pluginSettingsValues[item.key] ?? item.default ?? ''}
                        placeholder={item.placeholder || ''}
                        onChange={(e) => setPluginSettingsValues((prev) => ({ ...prev, [item.key]: item.type === 'number' ? Number(e.target.value) : e.target.value }))}
                      />
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
          <Modal
            title="配置源文件 (JSON)"
            open={showConfigModal}
            onCancel={() => setShowConfigModal(false)}
            width={700}
            footer={
              <Space>
                <Button onClick={() => setShowConfigModal(false)}>取消</Button>
                <Button
                  type="primary"
                  disabled={!!configJsonError}
                  onClick={async () => {
                    try {
                      const parsed = JSON.parse(configEditText);
                      await applicationsApi.update(appId!, { config: parsed });
                      message.success('配置保存成功');
                      fetchData();
                      setShowConfigModal(false);
                    } catch (e: any) {
                      message.error(e.response?.data?.detail || 'JSON 格式错误，保存失败');
                    }
                  }}
                >
                  保存
                </Button>
              </Space>
            }
          >
            <Input.TextArea
              value={configEditText}
              onChange={(e) => {
                setConfigEditText(e.target.value);
                try { JSON.parse(e.target.value); setConfigJsonError(''); }
                catch { setConfigJsonError('JSON 格式错误'); }
              }}
              style={{ fontFamily: 'monospace', fontSize: 13 }}
              autoSize={{ minRows: 10, maxRows: 24 }}
              status={configJsonError ? 'error' : undefined}
            />
            {configJsonError && <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>{configJsonError}</div>}
          </Modal>
        </>
      ),
    },
    {
      key: 'permissions',
      label: '访问权限',
      children: (
        <Card
          title="用户访问权限"
          extra={
            <Button icon={<UserAddOutlined />} type="primary" onClick={() => { permForm.resetFields(); setAddPermModal(true); }}>
              添加用户
            </Button>
          }
        >
          <p style={{ color: 'var(--apple-text-secondary)', marginBottom: 16 }}>
            管理员及以上角色默认可访问所有应用，此处为普通用户分配访问权限。
          </p>
          <Table
            dataSource={permissions}
            rowKey="id"
            pagination={false}
            columns={[
              {
                title: '用户',
                dataIndex: 'user_id',
                render: (uid: number) => {
                  const u = allUsers.find((u) => u.id === uid);
                  return u ? (
                    <Space>
                      <Avatar size="small">{u.name?.charAt(0)}</Avatar>
                      {u.name} ({u.username})
                    </Space>
                  ) : `用户 #${uid}`;
                },
              },
              {
                title: '操作',
                width: 80,
                render: (_: any, record: any) => (
                  <Popconfirm
                    title="确定移除该用户的访问权限？"
                    onConfirm={async () => {
                      try {
                        await applicationsApi.removePermission(appId!, record.id);
                        setPermissions((prev) => prev.filter((p) => p.id !== record.id));
                        message.success('已移除');
                      } catch (e: any) {
                        message.error(e.response?.data?.detail || '操作失败');
                      }
                    }}
                    okText="确定" cancelText="取消"
                  >
                    <Button type="link" danger size="small">移除</Button>
                  </Popconfirm>
                ),
              },
            ]}
          />
          <Modal
            title="添加用户权限"
            open={addPermModal}
            onCancel={() => setAddPermModal(false)}
            onOk={() => permForm.submit()}
          >
            <Form form={permForm} layout="vertical" onFinish={async (values) => {
              try {
                const res: any = await applicationsApi.addPermission(appId!, { user_id: values.user_id, role: 'user' });
                setPermissions((prev) => [...prev, res]);
                setAddPermModal(false);
                message.success('添加成功');
              } catch (e: any) {
                message.error(e.response?.data?.detail || '操作失败');
              }
            }}>
              <Form.Item name="user_id" label="选择用户" rules={[{ required: true, message: '请选择用户' }]}>
                <Select
                  showSearch
                  placeholder="搜索用户名或姓名"
                  optionFilterProp="label"
                  options={allUsers
                    .filter((u) => !permissions.some((p) => p.user_id === u.id))
                    .map((u) => ({ label: `${u.name} (${u.username})`, value: u.id }))}
                />
              </Form.Item>
            </Form>
          </Modal>
        </Card>
      ),
    },
  ];

  if (app.app_type === 'backend') {
    const cbUrl = (watchedCallback ?? app.config?.integration?.callback_url) || '<回调地址>';
    const hasSecret = !!(watchedSecret ?? app.config?.integration?.secret);
    const curlExample = `curl -X POST '${cbUrl}' \\\n`
      + `  -H 'Content-Type: application/json' \\\n`
      + (hasSecret ? `  -H 'X-Uzy-Signature: sha256=<对请求体的 HMAC-SHA256>' \\\n` : '')
      + `  -d '{"token":"<第三方token>","appId":${app.id},"action":"<action>"}'`;
    tabItems.splice(1, 0, {
      key: 'integration',
      label: '第三方对接',
      children: (
        <Card title="第三方对接（回调鉴权）">
          <Typography.Paragraph type="secondary">
            后端应用仅供第三方平台通过 sdk.js 调用。第三方调用<strong>带 token</strong> 的接口时，平台会把该 token
            连同 <code>appId</code>、<code>action</code> POST 到下方配置的回调地址进行校验：回调返回 HTTP 200（且 body 非 <code>{'{ valid: false }'}</code>）即视为通过，否则拒绝。
            <strong>未配置回调地址的后端应用，所有带 token 的调用都会被直接拒绝。</strong>
          </Typography.Paragraph>
          <Form form={integrationForm} layout="vertical" style={{ maxWidth: 640 }}>
            <Form.Item
              name="callback_url"
              label="回调地址 (callback_url)"
              extra="平台将以 POST 方式请求该地址，请求体为 { token, appId, action }。建议使用 HTTPS。"
              rules={[
                {
                  validator: async (_: any, v: string) => {
                    if (v && !/^https?:\/\//i.test(v.trim())) {
                      return Promise.reject('请输入以 http:// 或 https:// 开头的地址');
                    }
                  },
                },
              ]}
            >
              <Input placeholder="https://your-platform.com/uzy/verify" allowClear />
            </Form.Item>
            <Form.Item
              name="secret"
              label="签名密钥 (secret，可选)"
              extra="配置后，平台请求会带 X-Uzy-Signature: sha256=HMAC(secret, rawBody) 签名头，供第三方校验来源。"
            >
              <Input.Password placeholder="留空则不签名" autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              name="cache_ttl"
              label="校验缓存时长（秒）"
              extra="同一 token 校验通过后的缓存时间，避免每次调用都回调。默认 60 秒。"
            >
              <InputNumber min={0} max={3600} style={{ width: 160 }} />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={savingIntegration}
                onClick={async () => {
                  try {
                    const values = await integrationForm.validateFields();
                    const config = {
                      ...(app.config || {}),
                      integration: {
                        callback_url: (values.callback_url || '').trim(),
                        secret: values.secret || '',
                        cache_ttl: Number(values.cache_ttl) || 60,
                      },
                    };
                    setSavingIntegration(true);
                    await applicationsApi.update(appId!, { config });
                    message.success('对接配置已保存');
                    fetchData();
                  } catch (e: any) {
                    if (e?.errorFields) return;
                    message.error(e.response?.data?.detail || '保存失败');
                  } finally {
                    setSavingIntegration(false);
                  }
                }}
              >
                保存对接配置
              </Button>
            </Form.Item>
          </Form>

          <Typography.Title level={5} style={{ marginTop: 8 }}>回调请求示例</Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            平台会向你的回调地址发送如下请求，校验通过时请返回 HTTP 200（且 body 非 <code>{'{ valid: false }'}</code>）。
          </Typography.Paragraph>
          <div style={{ position: 'relative', maxWidth: 640 }}>
            <Button
              size="small"
              icon={<CopyOutlined />}
              style={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
              onClick={() => {
                navigator.clipboard?.writeText(curlExample).then(
                  () => message.success('已复制'),
                  () => message.error('复制失败'),
                );
              }}
            >复制</Button>
            <pre style={{
              background: 'var(--apple-bg-secondary, #f5f5f7)',
              padding: 16, borderRadius: 8, overflow: 'auto', fontSize: 12, margin: 0,
            }}>{curlExample}</pre>
          </div>
        </Card>
      ),
    });
  }

  return (
    <div className="apple-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Space align="center">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/applications')} />
          <h1 className="apple-page-title" style={{ marginBottom: 0 }}>{app.name}</h1>
        </Space>
        <Space>
          <Button icon={<EditOutlined />} onClick={handleEditInfo}>编辑</Button>
          <Popconfirm title="确定删除此应用？" onConfirm={handleDelete} okText="确定" cancelText="取消">
            <Button danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      </div>

      {editModal}

      <Tabs defaultActiveKey={defaultTab} items={tabItems} />
    </div>
  );
};

export default ApplicationDetail;
